import React, { useEffect, useMemo, useRef, useState } from 'react';
import { API_V1_BASE_URL } from '../../config/api';
import { formatAuditAction } from '../../config/auditActions';
import {
    Download,
    RefreshCw,
    Search,
    Building2,
    Zap,
    MapPin,
    Globe,
    Monitor,
    User as UserIcon,
    ShieldCheck,
} from 'lucide-react';

interface AuditLog {
    id: string;
    action: string;
    user_email?: string;
    performed_by_email?: string;
    performed_by_role?: string;
    details?: string;
    ip_address?: string;
    location_city?: string;
    location_country?: string;
    user_agent?: string;
    timestamp: string;
}

interface AuditTenant {
    id: string;
    name: string;
}

const AuditLogs: React.FC = () => {
    const [logs, setLogs] = useState<AuditLog[]>([]);
    const [tenants, setTenants] = useState<AuditTenant[]>([]);
    const [selectedTenantId, setSelectedTenantId] = useState('');
    const [selectedAction, setSelectedAction] = useState('');
    const [availableActions, setAvailableActions] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Dropdown open states + refs
    const [tenantDropdownOpen, setTenantDropdownOpen] = useState(false);
    const [actionDropdownOpen, setActionDropdownOpen] = useState(false);
    const tenantDropdownRef = useRef<HTMLDivElement | null>(null);
    const actionDropdownRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        loadAuditTenants();
        loadAuditActions();
        loadAuditLogs();
    }, []);

    // Close dropdowns on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (
                tenantDropdownRef.current &&
                !tenantDropdownRef.current.contains(e.target as Node)
            ) {
                setTenantDropdownOpen(false);
            }
            if (
                actionDropdownRef.current &&
                !actionDropdownRef.current.contains(e.target as Node)
            ) {
                setActionDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const loadAuditTenants = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_V1_BASE_URL}/audit-logs/tenants`, {
                headers: {
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            });
            const result = await response.json().catch(() => null);
            if (response.ok && result?.success) {
                setTenants(result.data || []);
            }
        } catch {
            setTenants([]);
        }
    };

    const loadAuditActions = async (tenantId = selectedTenantId) => {
        try {
            const token = localStorage.getItem('accessToken');
            const params = new URLSearchParams();
            if (tenantId) params.set('tenantId', tenantId);
            const response = await fetch(
                `${API_V1_BASE_URL}/audit-logs/actions?${params.toString()}`,
                { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
            );
            const result = await response.json().catch(() => null);
            setAvailableActions(
                response.ok && result?.success ? result.data || [] : []
            );
        } catch {
            setAvailableActions([]);
        }
    };

    const loadAuditLogs = async (
        tenantId = selectedTenantId,
        action = selectedAction
    ) => {
        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('accessToken');
            const params = new URLSearchParams({ limit: '200' });
            if (tenantId) params.set('tenantId', tenantId);
            if (action) params.set('action', action);
            const response = await fetch(
                `${API_V1_BASE_URL}/audit-logs?${params.toString()}`,
                {
                    headers: {
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                }
            );
            const result = await response.json().catch(() => null);

            if (!response.ok || !result?.success) {
                throw new Error(result?.message || 'Failed to load audit logs');
            }

            setLogs(result.data?.data || []);
        } catch (err) {
            setLogs([]);
            setError(
                err instanceof Error ? err.message : 'Failed to load audit logs'
            );
        } finally {
            setLoading(false);
        }
    };

    const getActionStyle = (action: string) => {
        if (action === 'LOGIN')
            return 'bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-700 ring-1 ring-emerald-300';
        if (action === 'LOGOUT')
            return 'bg-gradient-to-r from-slate-100 to-gray-100 text-gray-700 ring-1 ring-gray-300';
        if (action.includes('FAILED'))
            return 'bg-gradient-to-r from-rose-100 to-red-100 text-red-700 ring-1 ring-red-300';
        if (action.includes('GLACIER'))
            return 'bg-gradient-to-r from-indigo-100 to-blue-100 text-indigo-700 ring-1 ring-indigo-300';
        if (action.includes('UPLOAD'))
            return 'bg-gradient-to-r from-blue-100 to-sky-100 text-blue-700 ring-1 ring-blue-300';
        if (action.includes('DELETE'))
            return 'bg-gradient-to-r from-rose-100 to-pink-100 text-rose-700 ring-1 ring-rose-300';
        if (action.includes('CREATE'))
            return 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 ring-1 ring-blue-300';
        if (action.includes('UPDATE'))
            return 'bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700 ring-1 ring-amber-300';
        return 'bg-gradient-to-r from-purple-100 to-fuchsia-100 text-purple-700 ring-1 ring-purple-300';
    };

    const getActionDot = (action: string) => {
        if (action === 'LOGIN') return 'bg-emerald-500 animate-pulse';
        if (action === 'LOGOUT') return 'bg-gray-500';
        if (action.includes('FAILED')) return 'bg-red-500';
        if (action.includes('GLACIER')) return 'bg-indigo-500';
        if (action.includes('UPLOAD')) return 'bg-blue-500';
        if (action.includes('DELETE')) return 'bg-rose-500';
        if (action.includes('CREATE')) return 'bg-blue-500';
        if (action.includes('UPDATE')) return 'bg-amber-500';
        return 'bg-purple-500';
    };

    const exportAuditLogs = () => {
        if (logs.length === 0) {
            alert('No audit logs to export.');
            return;
        }

        const rows = logs.map((log) => ({
            Action: log.action,
            'User Email': log.user_email || 'N/A',
            'Performed By': log.performed_by_email || 'N/A',
            'Performer Role': log.performed_by_role || 'N/A',
            Details: log.details || '',
            'IP Address': log.ip_address || 'N/A',
            Location:
                [log.location_city, log.location_country]
                    .filter(Boolean)
                    .join(', ') || 'N/A',
            'User Agent': log.user_agent || 'N/A',
            Date: new Date(log.timestamp).toLocaleDateString(),
            Time: new Date(log.timestamp).toLocaleTimeString(),
            Timestamp: new Date(log.timestamp).toISOString(),
        }));

        const headers = Object.keys(rows[0]);
        const csv = [
            headers.join(','),
            ...rows.map((row) =>
                headers
                    .map(
                        (header) =>
                            `"${String(row[header as keyof typeof row]).replace(
                                /"/g,
                                '""'
                            )}"`
                    )
                    .join(',')
            ),
        ].join('\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `super_admin_audit_logs_${
            new Date().toISOString().split('T')[0]
        }.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
    };

    // Local search filter (overrides server response for a quick in-page search)
    const filteredLogs = useMemo(() => {
        const s = searchTerm.trim().toLowerCase();
        if (!s) return logs;
        return logs.filter((log) => {
            return (
                (log.action || '').toLowerCase().includes(s) ||
                (log.user_email || '').toLowerCase().includes(s) ||
                (log.performed_by_email || '').toLowerCase().includes(s) ||
                (log.performed_by_role || '').toLowerCase().includes(s) ||
                (log.details || '').toLowerCase().includes(s) ||
                (log.ip_address || '').toLowerCase().includes(s) ||
                `${log.location_city || ''} ${log.location_country || ''}`
                    .toLowerCase()
                    .includes(s)
            );
        });
    }, [logs, searchTerm]);

    const selectedTenantName =
        tenants.find((t) => t.id === selectedTenantId)?.name || 'All Organizations';
    const selectedActionLabel = selectedAction
        ? formatAuditAction(selectedAction)
        : 'All Actions';

    const hasActiveFilters =
        selectedTenantId !== '' || selectedAction !== '' || searchTerm !== '';

    const clearFilters = () => {
        setSelectedTenantId('');
        setSelectedAction('');
        setSearchTerm('');
        loadAuditActions('');
        loadAuditLogs('', '');
    };

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                {/* Header */}
                <div className="px-4 py-3 border-b border-blue-100 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-sm shadow-sm">
                            📋
                        </div>
                        <div>
                            <h2 className="text-sm font-semibold text-gray-900">
                                Audit Logs
                            </h2>
                            <p className="text-xs text-blue-700/70">
                                All system audit activity from PostgreSQL
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={exportAuditLogs}
                            disabled={loading || logs.length === 0}
                            className="group inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold
                bg-gradient-to-r from-emerald-600 to-green-600 text-white
                border border-emerald-700 shadow-sm
                hover:from-emerald-700 hover:to-green-700 hover:shadow-md
                active:scale-[0.98]
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-all duration-200"
                        >
                            <Download
                                size={16}
                                className="text-emerald-100 group-hover:text-white transition-colors"
                            />
                            <span>Export CSV</span>
                        </button>
                        <button
                            onClick={() => loadAuditLogs()}
                            disabled={loading}
                            className="group inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold
                bg-gradient-to-r from-blue-600 to-indigo-600 text-white
                border border-blue-700 shadow-sm
                hover:from-blue-700 hover:to-indigo-700 hover:shadow-md
                active:scale-[0.98]
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-all duration-200"
                        >
                            <RefreshCw
                                size={16}
                                className={`text-blue-100 group-hover:text-white transition-colors ${
                                    loading ? 'animate-spin' : ''
                                }`}
                            />
                            <span>{loading ? 'Refreshing...' : 'Refresh'}</span>
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="p-4 border-b border-blue-100 bg-white">
                    <div className="flex flex-wrap gap-3 items-center">
                        {/* Tenant dropdown */}
                        <div ref={tenantDropdownRef} className="relative inline-block">
                            <button
                                type="button"
                                onClick={() => setTenantDropdownOpen((p) => !p)}
                                className={`w-56 flex items-center justify-between gap-3 px-4 py-2.5
                  bg-white border rounded-xl text-sm font-medium
                  transition-all duration-200
                  ${
                      tenantDropdownOpen
                          ? 'border-blue-500 ring-2 ring-blue-100 shadow-sm'
                          : 'border-gray-200 hover:border-gray-300'
                  }`}
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    <Building2 size={16} className="text-gray-400 shrink-0" />
                                    <span className="truncate text-gray-700">
                                        {selectedTenantName}
                                    </span>
                                </div>
                                <span
                                    className={`text-gray-400 transition-transform duration-200 ${
                                        tenantDropdownOpen ? 'rotate-180' : ''
                                    }`}
                                >
                                    ▾
                                </span>
                            </button>

                            {tenantDropdownOpen && (
                                <div className="absolute z-50 mt-2 left-0 w-56 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                                    <div className="max-h-72 overflow-y-auto p-1.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSelectedTenantId('');
                                                setSelectedAction('');
                                                setTenantDropdownOpen(false);
                                                loadAuditActions('');
                                                loadAuditLogs('', '');
                                            }}
                                            className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${
                                                selectedTenantId === ''
                                                    ? 'bg-blue-50 text-blue-700 font-semibold'
                                                    : 'text-gray-700 hover:bg-gray-50'
                                            }`}
                                        >
                                            <span>All Organizations</span>
                                            {selectedTenantId === '' && (
                                                <span className="text-blue-600 shrink-0">✓</span>
                                            )}
                                        </button>

                                        {tenants.map((tenant) => {
                                            const isSelected = selectedTenantId === tenant.id;
                                            return (
                                                <button
                                                    key={tenant.id}
                                                    type="button"
                                                    onClick={() => {
                                                        setSelectedTenantId(tenant.id);
                                                        setSelectedAction('');
                                                        setTenantDropdownOpen(false);
                                                        loadAuditActions(tenant.id);
                                                        loadAuditLogs(tenant.id, '');
                                                    }}
                                                    className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${
                                                        isSelected
                                                            ? 'bg-blue-50 text-blue-700 font-semibold'
                                                            : 'text-gray-700 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    <span className="truncate">{tenant.name}</span>
                                                    {isSelected && (
                                                        <span className="text-blue-600 shrink-0">✓</span>
                                                    )}
                                                </button>
                                            );
                                        })}

                                        {tenants.length === 0 && (
                                            <div className="px-3 py-3 text-sm text-gray-500 text-center">
                                                No organizations available
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Action dropdown */}
                        <div ref={actionDropdownRef} className="relative inline-block">
                            <button
                                type="button"
                                onClick={() => setActionDropdownOpen((p) => !p)}
                                className={`w-56 flex items-center justify-between gap-3 px-4 py-2.5
                  bg-white border rounded-xl text-sm font-medium
                  transition-all duration-200
                  ${
                      actionDropdownOpen
                          ? 'border-blue-500 ring-2 ring-blue-100 shadow-sm'
                          : 'border-gray-200 hover:border-gray-300'
                  }`}
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    <Zap size={16} className="text-gray-400 shrink-0" />
                                    <span className="truncate text-gray-700">
                                        {selectedActionLabel}
                                    </span>
                                </div>
                                <span
                                    className={`text-gray-400 transition-transform duration-200 ${
                                        actionDropdownOpen ? 'rotate-180' : ''
                                    }`}
                                >
                                    ▾
                                </span>
                            </button>

                            {actionDropdownOpen && (
                                <div className="absolute z-50 mt-2 left-0 w-56 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                                    <div className="max-h-72 overflow-y-auto p-1.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSelectedAction('');
                                                setActionDropdownOpen(false);
                                                loadAuditLogs(selectedTenantId, '');
                                            }}
                                            className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${
                                                selectedAction === ''
                                                    ? 'bg-blue-50 text-blue-700 font-semibold'
                                                    : 'text-gray-700 hover:bg-gray-50'
                                            }`}
                                        >
                                            <span>All Actions</span>
                                            {selectedAction === '' && (
                                                <span className="text-blue-600 shrink-0">✓</span>
                                            )}
                                        </button>

                                        {availableActions.map((action) => {
                                            const isSelected = selectedAction === action;
                                            return (
                                                <button
                                                    key={action}
                                                    type="button"
                                                    onClick={() => {
                                                        setSelectedAction(action);
                                                        setActionDropdownOpen(false);
                                                        loadAuditLogs(selectedTenantId, action);
                                                    }}
                                                    className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${
                                                        isSelected
                                                            ? 'bg-blue-50 text-blue-700 font-semibold'
                                                            : 'text-gray-700 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    <span className="truncate">
                                                        {formatAuditAction(action)}
                                                    </span>
                                                    {isSelected && (
                                                        <span className="text-blue-600 shrink-0">✓</span>
                                                    )}
                                                </button>
                                            );
                                        })}

                                        {availableActions.length === 0 && (
                                            <div className="px-3 py-3 text-sm text-gray-500 text-center">
                                                No actions available
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Search */}
                        <div className="relative">
                            <Search
                                size={16}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                            />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search logs..."
                                className="pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64 transition"
                            />
                        </div>

                        {hasActiveFilters && (
                            <button
                                onClick={clearFilters}
                                className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 transition flex items-center gap-2 text-sm font-medium"
                            >
                                ✕ Clear Filters
                            </button>
                        )}
                    </div>
                </div>

                {/* Body */}
                <div className="p-4 bg-gray-50/40">
                    {loading ? (
                        <div className="text-center py-16">
                            <div className="inline-block w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                            <p className="text-sm text-gray-500 mt-3">Loading audit logs...</p>
                        </div>
                    ) : error ? (
                        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
                            {error}
                        </div>
                    ) : filteredLogs.length === 0 ? (
                        <div className="text-center py-16">
                            <div className="text-5xl mb-3">📋</div>
                            <p className="text-gray-600 font-medium">No audit logs found</p>
                            <p className="text-sm text-gray-400 mt-1">
                                Try changing your filters
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1 [scrollbar-width:thin]">
                            {filteredLogs.map((log, idx) => (
                                <div
                                    key={log.id}
                                    className={`p-4 border rounded-xl transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${
                                        idx % 2 === 0
                                            ? 'bg-white border-blue-50 hover:border-blue-200'
                                            : 'bg-blue-50/30 border-blue-50 hover:border-blue-200'
                                    }`}
                                >
                                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-2 mb-2">
                                                <span
                                                    className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full font-semibold capitalize shadow-sm ${getActionStyle(
                                                        log.action
                                                    )}`}
                                                >
                                                    <span
                                                        className={`w-1.5 h-1.5 rounded-full ${getActionDot(
                                                            log.action
                                                        )}`}
                                                    />
                                                    {formatAuditAction(log.action)}
                                                </span>
                                                <span className="text-sm font-semibold text-gray-900">
                                                    {log.user_email || 'Unknown user'}
                                                </span>
                                            </div>

                                            <p className="text-sm text-gray-700 mb-2">
                                                {log.details || 'No details'}
                                            </p>

                                            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 text-xs text-gray-600">
                                                <div className="flex items-center gap-1.5">
                                                    <UserIcon size={12} className="text-gray-400 shrink-0" />
                                                    <span className="truncate">
                                                        {log.performed_by_email || 'N/A'}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    <ShieldCheck size={12} className="text-gray-400 shrink-0" />
                                                    <span className="truncate">
                                                        {formatAuditAction(log.performed_by_role || 'N/A')}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    <Globe size={12} className="text-gray-400 shrink-0" />
                                                    <span className="truncate">
                                                        {log.ip_address || 'N/A'}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    <MapPin size={12} className="text-gray-400 shrink-0" />
                                                    <span className="truncate">
                                                        {[log.location_city, log.location_country]
                                                            .filter(Boolean)
                                                            .join(', ') || 'N/A'}
                                                    </span>
                                                </div>
                                            </div>

                                            {log.user_agent && (
                                                <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-400">
                                                    <Monitor size={12} className="shrink-0" />
                                                    <span className="truncate" title={log.user_agent}>
                                                        {log.user_agent}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="text-left lg:text-right shrink-0 lg:min-w-[130px] lg:border-l lg:pl-4 border-gray-100">
                                            <div className="text-xs text-gray-400">
                                                Date
                                            </div>
                                            <div className="text-sm font-semibold text-gray-800">
                                                {new Date(log.timestamp).toLocaleDateString()}
                                            </div>
                                            <div className="mt-1 text-xs text-gray-400">
                                                Time
                                            </div>
                                            <div className="text-sm font-medium text-gray-700">
                                                {new Date(log.timestamp).toLocaleTimeString([], {
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AuditLogs;